import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ChallengeListResponse } from "@versedrill/core";
import { Button } from "@/components/ui/button";
import { Countdown } from "@/components/Countdown.tsx";
import { Masthead } from "@/components/Masthead.tsx";
import { ErrorNote, LoadingLeaf } from "@/components/Notices.tsx";
import { api, ApiError } from "@/lib/api.ts";
import { useStore } from "@/lib/store.ts";
import { useRetryWhenOnline } from "@/lib/useInstall.ts";
import { GamePage, newestUnplayed } from "./GamePage.tsx";

/** "/" → the newest verse you haven't finished. Resolved once, so finishing a game doesn't swap the page under you. */
export function HomePage() {
  const [list, setList] = useState<ChallengeListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const stars = useStore((s) => s.stars);
  const [target, setTarget] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    setError(null);
    api
      .list()
      .then((l) => {
        setList(l);
        setTarget((t) => (t === undefined ? newestUnplayed(l, useStore.getState().stars) : t));
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Couldn't load today's verse."));
  }, [attempt]);

  useRetryWhenOnline(error !== null, () => setAttempt((a) => a + 1));

  if (error) {
    return (
      <div className="min-h-dvh">
        <Masthead />
        <div className="mx-auto max-w-2xl px-5 pt-8 sm:px-8">
          <ErrorNote message={error} onRetry={() => setAttempt((a) => a + 1)} />
        </div>
      </div>
    );
  }
  if (!list || target === undefined) return (
    <div className="min-h-dvh">
      <Masthead />
      <LoadingLeaf />
    </div>
  );
  if (target !== null) return <GamePage key={target} number={target} />;

  const played = Object.keys(stars).length;
  return (
    <div className="min-h-dvh">
      <Masthead />
      <main className="animate-leaf-in mx-auto flex max-w-xl flex-col items-center gap-5 px-5 pt-16 text-center sm:px-8">
        <p className="font-ui text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-found">{list.latest === 0 ? "Coming soon" : "All caught up"}</p>
        <h1 className="font-display text-5xl italic leading-[1.02] sm:text-6xl">{list.latest === 0 ? "The first verse hasn't been set yet." : "You've read every verse we've set."}</h1>
        {list.latest > 0 && (
          <>
            <p className="font-body text-lg italic text-muted-foreground">
              {played} verses solved. The next one arrives in <Countdown onZero={() => setAttempt((a) => a + 1)} />.
            </p>
            <Button asChild variant="outline" size="lg" className="h-12">
              <Link to="/challenges">Replay an earlier verse</Link>
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
